import { lazy, Suspense } from "react";
import { createBrowserRouter, useRouteError } from "react-router";
import PublicLayout from "./layouts/PublicLayout";
import DashboardLayout from "./layouts/DashboardLayout";
import ProtectedRoute from "./components/ProtectedRoute";
import HomeEager from "./pages/Home";

const PageError = () => {
  const error = useRouteError() as Error;
  const isChunkError = error?.message?.includes("Failed to fetch dynamically imported module")
    || error?.message?.includes("Importing a module script failed");
  return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] px-6 text-center">
      <div className="text-4xl mb-4">😕</div>
      <h2 className="text-lg font-semibold text-gray-800 mb-2">
        {isChunkError ? "Halaman gagal dimuat" : "Terjadi kesalahan"}
      </h2>
      <p className="text-sm text-gray-500 mb-6">
        {isChunkError
          ? "Koneksi internet terputus atau ada pembaruan sistem. Coba muat ulang halaman."
          : "Silakan coba lagi atau kembali ke halaman sebelumnya."}
      </p>
      <button
        onClick={() => window.location.reload()}
        className="px-4 py-2 bg-[#003580] text-white text-sm rounded-lg"
      >
        Muat Ulang
      </button>
    </div>
  );
};

// Spinner ditampilkan saat chunk halaman sedang dimuat
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-[60vh]">
    <div className="w-7 h-7 border-4 border-gray-200 border-t-[#003580] rounded-full animate-spin" />
  </div>
);

// Bungkus lazy component dengan Suspense
const s = (C: React.LazyExoticComponent<React.ComponentType>) => () => (
  <Suspense fallback={<PageLoader />}><C /></Suspense>
);

// Halaman publik
const BukuTamu     = s(lazy(() => import("./pages/BukuTamu")));
const Penilaian    = s(lazy(() => import("./pages/Penilaian")));
const Pengaduan    = s(lazy(() => import("./pages/Pengaduan")));
const Login        = s(lazy(() => import("./pages/Login")));
const AntrianDisplay = s(lazy(() => import("./pages/AntrianDisplay")));
const QRDisplay    = s(lazy(() => import("./pages/QRDisplay")));
const NotFound     = s(lazy(() => import("./pages/NotFound")));

// Halaman petugas
const PetugasDashboard  = s(lazy(() => import("./pages/petugas/Dashboard")));
const Presensi          = s(lazy(() => import("./pages/petugas/Presensi")));
const ManajemenLayanan  = s(lazy(() => import("./pages/petugas/ManajemenLayanan")));
const KonsultasiForm    = s(lazy(() => import("./pages/petugas/KonsultasiForm")));
const WhatsApp          = s(lazy(() => import("./pages/petugas/WhatsApp")));
const DaftarTamu        = s(lazy(() => import("./pages/petugas/DaftarTamu")));
const BankData          = s(lazy(() => import("./pages/petugas/BankData")));

// Halaman admin
const AdminDashboard    = s(lazy(() => import("./pages/admin/Dashboard")));
const ManajemenPetugas  = s(lazy(() => import("./pages/admin/ManajemenPetugas")));
const DataPengunjung    = s(lazy(() => import("./pages/admin/DataPengunjung")));
const AdminPengaduan    = s(lazy(() => import("./pages/admin/Pengaduan")));
const Laporan           = s(lazy(() => import("./pages/admin/Laporan")));
const AdminWhatsApp     = s(lazy(() => import("./pages/admin/WhatsApp")));

export const router = createBrowserRouter([
  {
    path: "/",
    Component: PublicLayout,
    ErrorBoundary: PageError,
    children: [
      { index: true, Component: HomeEager },
      { path: "buku-tamu", Component: BukuTamu },
      { path: "penilaian", Component: Penilaian },
      { path: "pengaduan", Component: Pengaduan },
      { path: "login", Component: Login },
    ],
  },
  { path: "/antrian", Component: AntrianDisplay, ErrorBoundary: PageError },
  { path: "/qr", Component: QRDisplay, ErrorBoundary: PageError },
  { path: "*", Component: NotFound },
  {
    path: "/petugas",
    Component: DashboardLayout,
    ErrorBoundary: PageError,
    children: [
      {
        Component: () => <ProtectedRoute allowedRole="petugas" />,
        children: [
          { index: true, Component: PetugasDashboard },
          { path: "presensi", Component: Presensi },
          { path: "layanan", Component: ManajemenLayanan },
          { path: "konsultasi", Component: KonsultasiForm },
          { path: "whatsapp", Component: WhatsApp },
          { path: "daftar-tamu", Component: DaftarTamu },
          { path: "bank-data", Component: BankData },
        ],
      },
    ],
  },
  {
    path: "/admin",
    Component: DashboardLayout,
    ErrorBoundary: PageError,
    children: [
      {
        Component: () => <ProtectedRoute allowedRole="admin" />,
        children: [
          { index: true, Component: AdminDashboard },
          { path: "pengunjung", Component: DataPengunjung },
          { path: "petugas", Component: ManajemenPetugas },
          { path: "pengaduan", Component: AdminPengaduan },
          { path: "laporan", Component: Laporan },
          { path: "whatsapp", Component: AdminWhatsApp },
          { path: "bank-data", Component: BankData },
        ],
      },
    ],
  },
]);
